import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Globe, Lock, Play, Clock, Trash2, Pencil, Calendar, Star, Filter } from "lucide-react";
import { useSavedQueries, useDeleteSavedQuery, useTrackQueryExecution } from "@/hooks/savedQueries";
import { useAuth } from "@/providers/AuthProvider";
import { SavedQuery } from "@/types/savedQuery";
import { formatDistanceToNow, format } from "date-fns";
import { ViewToggle, ViewMode } from "@/components/common/ViewToggle";
import { usePersistedViewMode } from "@/utils/viewMode";
import { useUserFavorites } from "@/hooks/favorites/useUserFavorites";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { useEntityHistory } from "@/hooks/useEntityHistory";
import {
	ToggleGroup,
	ToggleGroupItem,
} from "@/components/ui/toggle-group";

type QueryType = 'advanced' | 'simple';

export function QueriesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [search, setSearch] = useState('');
	const [queryToDelete, setQueryToDelete] = useState<SavedQuery | null>(null);
	const [viewMode, setViewMode] = usePersistedViewMode("queries", "card");
	const [queryTypeFilter, setQueryTypeFilter] = useState<QueryType | 'all'>('all');

	const { data: savedQueries = [], isLoading } = useSavedQueries();
	const deleteQuery = useDeleteSavedQuery();
	const trackExecution = useTrackQueryExecution();
	const { favorites } = useUserFavorites('query');
	const { trackVisit } = useEntityHistory();

	// Helper to determine query type
	const getQueryType = (query: SavedQuery): QueryType => {
		const def = query.query_definition as any;
		if (def && 'rootGroup' in def)
			return 'advanced';
		return 'simple';
	};

	// Filter based on query type
	const typeFilteredQueries = queryTypeFilter === 'all' 
		? savedQueries 
		: savedQueries.filter(q => getQueryType(q) === queryTypeFilter);

	const filteredQueries = typeFilteredQueries.filter(q =>
		q.name.toLowerCase().includes(search.toLowerCase()) ||
		(q.description?.toLowerCase().includes(search.toLowerCase()))
	);

	// Group by ownership
	const myQueries = filteredQueries.filter(q => q.created_by === user?.id);
	const publicQueries = filteredQueries.filter(q => q.is_public && q.created_by !== user?.id);

	const handleRunQuery = async (query: SavedQuery) => {
		trackExecution.mutate(query.id);
		trackVisit('query', query.id, query.name);
		
		const queryType = getQueryType(query);
		if (queryType === 'simple') {
			// For simple filter queries, navigate to candidates page with filters applied
			const def = query.query_definition as any;
			const params = new URLSearchParams();
			if (def.searchTerm) params.set('search', def.searchTerm);
			if (def.skillsFilter?.length > 0) params.set('skills', def.skillsFilter.join(','));
			if (def.skillsOperator && def.skillsOperator !== 'AND') params.set('skillsOp', def.skillsOperator);
			if (def.statusFilter?.length > 0) params.set('status', def.statusFilter.join(','));
			if (def.functionFilter) params.set('function', def.functionFilter);
			if (def.assignedToMe) params.set('assignedToMe', 'true');
			if (def.showFavoritesOnly) params.set('favoritesOnly', 'true');
			if (def.sortField && def.sortField !== 'name') params.set('sortField', def.sortField);
			if (def.sortOrder && def.sortOrder !== 'asc') params.set('sortOrder', def.sortOrder);
			navigate(`/candidates?${params.toString()}`);
		} else {
			// For advanced queries, go to query detail page
			navigate(`/queries/${query.id}`);
		}
	};

	const handleEditQuery = (query: SavedQuery) => {
		const queryType = getQueryType(query);
		if (queryType === 'simple') {
			// For simple filter queries, navigate to candidates page with filters
			const def = query.query_definition as any;
			const params = new URLSearchParams();
			if (def.searchTerm) params.set('search', def.searchTerm);
			if (def.skillsFilter?.length > 0) params.set('skills', def.skillsFilter.join(','));
			if (def.skillsOperator && def.skillsOperator !== 'AND') params.set('skillsOp', def.skillsOperator);
			if (def.statusFilter?.length > 0) params.set('status', def.statusFilter.join(','));
			if (def.functionFilter) params.set('function', def.functionFilter);
			if (def.assignedToMe) params.set('assignedToMe', 'true');
			if (def.showFavoritesOnly) params.set('favoritesOnly', 'true');
			if (def.sortField && def.sortField !== 'name') params.set('sortField', def.sortField);
			if (def.sortOrder && def.sortOrder !== 'asc') params.set('sortOrder', def.sortOrder);
			navigate(`/candidates?${params.toString()}`);
		} else {
			navigate(`/queries/${query.id}`);
		}
	};

	const handleDeleteQuery = async () => {
		if (queryToDelete) {
			await deleteQuery.mutateAsync(queryToDelete.id);
			setQueryToDelete(null);
		}
	};

	const handleCreateNew = () => {
		navigate('/queries/new');
	};

	// Grid Card View
	const QueryGridCard = ({ query }: { query: SavedQuery }) => {
		const isOwner = query.created_by === user?.id;
		const queryType = getQueryType(query);

		return (
			<Card
				className="hover:shadow-md transition-all duration-200 cursor-pointer group"
				onClick={() => handleRunQuery(query)}
			>
				<CardContent className="p-4">
					{/* Header with icon and actions */}
					<div className="flex items-start justify-between mb-3">
						<div className="flex items-center gap-2">
							<Avatar className="h-10 w-10">
								<AvatarFallback className={query.is_public ? "bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"}>
									{query.is_public ? <Globe className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
								</AvatarFallback>
							</Avatar>
							{favorites.has(query.id) && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
						</div>
						<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
							<FavoriteButton entityType="query" entityId={query.id} size="sm" />
							{isOwner && (
								<>
									<Button
										size="sm"
										variant="ghost"
										className="h-8 w-8 p-0"
										onClick={() => handleEditQuery(query)}
									>
										<Pencil className="h-4 w-4" />
									</Button>
									<Button
										size="sm"
										variant="ghost"
										className="h-8 w-8 p-0 text-gray-400 dark:text-gray-500 hover:text-red-600"
										onClick={() => setQueryToDelete(query)}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</>
							)}
						</div>
					</div>

					{/* Title */}
					<div className="flex items-center gap-2 mb-1">
						<h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate flex-1">{query.name}</h3>
						<Badge variant={queryType === 'advanced' ? 'default' : 'secondary'} className="text-xs">
							{queryType === 'advanced' ? 'Advanced' : 'Filter'}
						</Badge>
					</div>

					{/* Description */}
					{query.description && (
						<p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3 min-h-[2.5rem]">
							{query.description}
						</p>
					)}

					{/* Stats */}
					<div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
						<span className="flex items-center gap-1">
							<Play className="h-3 w-3" />
							{query.execution_count} {t("queries.list.executions", "runs")}
						</span>
						{query.last_executed_at && (
							<span className="flex items-center gap-1">
								<Clock className="h-3 w-3" />
								{formatDistanceToNow(new Date(query.last_executed_at), { addSuffix: true })}
							</span>
						)}
					</div>

					{/* Footer */}
					<div className="flex items-center justify-between pt-3 border-t">
						<div className="flex items-center gap-2 text-xs text-gray-400">
							<Calendar className="h-3 w-3" />
							{format(new Date(query.created_at), 'MMM d, yyyy')}
						</div>
						<Button size="sm" onClick={(e) => { e.stopPropagation(); handleRunQuery(query); }}>
							<Play className="h-3 w-3 mr-1" />
							{t("queries.list.run", "Run")}
						</Button>
					</div>
				</CardContent>
			</Card>
		);
	};

	// List View Row
	const QueryListRow = ({ query }: { query: SavedQuery }) => {
		const isOwner = query.created_by === user?.id;
		const queryType = getQueryType(query);

		return (
			<Card className="hover:shadow-md transition-shadow">
				<CardContent className="p-4">
					<div className="flex items-center gap-4">
						{/* Icon */}
						<Avatar className="h-10 w-10 shrink-0">
							<AvatarFallback className={query.is_public ? "bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"}>
								{query.is_public ? <Globe className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
							</AvatarFallback>
						</Avatar>

						{/* Content */}
						<div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleRunQuery(query)}>
							<div className="flex items-center gap-2 mb-1">
								<h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{query.name}</h3>
								<Badge variant={queryType === 'advanced' ? 'default' : 'secondary'} className="text-xs">
									{queryType === 'advanced' ? 'Advanced' : 'Filter'}
								</Badge>
							</div>
							{query.description && (
								<p className="text-sm text-gray-500 dark:text-gray-400 truncate">
									{query.description}
								</p>
							)}
						</div>

						{/* Stats */}
						<div className="hidden md:flex items-center gap-6 text-sm text-gray-500 shrink-0">
							<span className="flex items-center gap-1">
								<Play className="h-4 w-4" />
								{query.execution_count}
							</span>
							{query.last_executed_at && (
								<span className="flex items-center gap-1 min-w-[100px]">
									<Clock className="h-4 w-4" />
									{formatDistanceToNow(new Date(query.last_executed_at), { addSuffix: true })}
								</span>
							)}
							<span className="flex items-center gap-1 min-w-[90px]">
								<Calendar className="h-4 w-4" />
								{format(new Date(query.created_at), 'MMM d, yyyy')}
							</span>
						</div>

						{/* Actions */}
						<div className="flex items-center gap-2 shrink-0">
							<Button
								size="sm"
								onClick={() => handleRunQuery(query)}
							>
								<Play className="h-4 w-4 mr-1" />
								{t("queries.list.run", "Run")}
							</Button>
							{isOwner && (
								<>
									<Button
										size="sm"
										variant="outline"
										onClick={() => handleEditQuery(query)}
									>
										<Pencil className="h-4 w-4" />
									</Button>
									<Button
										size="sm"
										variant="ghost"
										className="text-gray-400 dark:text-gray-500 hover:text-red-600"
										onClick={() => setQueryToDelete(query)}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</>
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		);
	};

	const renderQueries = (queries: SavedQuery[]) => {
		if (viewMode === "card") {
			return (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
					{queries.map((query) => (
						<QueryGridCard key={query.id} query={query} />
					))}
				</div>
			);
		}
		return (
			<div className="space-y-3">
				{queries.map((query) => (
					<QueryListRow key={query.id} query={query} />
				))}
			</div>
		);
	};

	return (
		<div>
			<PageHeader
				title={t("queries.title", "Queries")}
				actions={
					<Button onClick={handleCreateNew}>
						<Plus className="h-4 w-4 mr-2" />
						{t("queries.actions.create", "New Query")}
					</Button>
				}
			/>

			{/* Search Bar, Filters and View Toggle */}
			<div className="px-6 py-4">
				<div className="flex items-center justify-between gap-4">
					<div className="flex items-center gap-3 flex-1">
						<div className="relative flex-1 max-w-md">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
							<Input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder={t("queries.list.searchPlaceholder", "Search queries...")}
								className="pl-10"
							/>
						</div>
						<ToggleGroup type="single" value={queryTypeFilter} onValueChange={(value) => value && setQueryTypeFilter(value as QueryType | 'all')}>
							<ToggleGroupItem value="advanced" aria-label="Show advanced queries">
								Advanced
							</ToggleGroupItem>
							<ToggleGroupItem value="simple" aria-label="Show filter queries">
								Filters
							</ToggleGroupItem>
							<ToggleGroupItem value="all" aria-label="Show all queries">
								All
							</ToggleGroupItem>
						</ToggleGroup>
					</div>
					<ViewToggle
						viewMode={viewMode}
						onViewModeChange={setViewMode}
						label={t("sortBar.view", "View")}
					/>
				</div>
			</div>

			{/* Content */}
			<div className="px-6 pb-6">
				{isLoading ? (
					viewMode === "card" ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
							{Array.from({ length: 8 }).map((_, i) => (
								<Skeleton key={i} className="h-48 w-full" />
							))}
						</div>
					) : (
						<div className="space-y-3">
							{Array.from({ length: 5 }).map((_, i) => (
								<Skeleton key={i} className="h-20 w-full" />
							))}
						</div>
					)
				) : filteredQueries.length === 0 ? (
					<Card className="border-dashed border-2">
						<CardContent className="p-12 text-center">
							<Search className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
							<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
								{search
									? t("queries.list.noResults", "No queries found")
									: t("queries.list.empty", "No saved queries yet")}
							</h3>
							<p className="text-gray-500 dark:text-gray-400 mb-6">
								{search
									? t("queries.list.noResultsDescription", "Try adjusting your search terms")
									: t("queries.list.emptyDescription", "Create your first query to start searching for candidates")}
							</p>
							{!search && (
								<Button onClick={handleCreateNew}>
									<Plus className="h-4 w-4 mr-2" />
									{t("queries.actions.create", "New Query")}
								</Button>
							)}
						</CardContent>
					</Card>
				) : (
					<div className="space-y-8">
						{/* My Queries Section */}
						{myQueries.length > 0 && (
							<div>
								<div className="flex items-center gap-2 mb-4">
									<h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
										{t("queries.list.myQueries", "My Queries")}
									</h2>
									<Badge variant="secondary">{myQueries.length}</Badge>
								</div>
								{renderQueries(myQueries)}
							</div>
						)}

						{/* Public Queries Section */}
						{publicQueries.length > 0 && (
							<div>
								<div className="flex items-center gap-2 mb-4">
									<h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
										{t("queries.list.publicQueries", "Public Queries")}
									</h2>
									<Badge variant="outline">{publicQueries.length}</Badge>
								</div>
								{renderQueries(publicQueries)}
							</div>
						)}
					</div>
				)}
			</div>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={!!queryToDelete} onOpenChange={() => setQueryToDelete(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("queries.delete.title", "Delete Query")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("queries.delete.description", "Are you sure you want to delete this saved query? This action cannot be undone.")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("common.cancel", "Cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteQuery}
							className="bg-red-600 hover:bg-red-700"
						>
							{t("common.delete", "Delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
